import {
  QuoteMode,
  parseSuggestedQuoteMode,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { TransportType } from 'types/QuoteRequest';
import { Maybe } from 'types/UtilityTypes';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import { LoadBuildingSuggestions } from 'types/suggestions/LoadBuildingSuggestions';
import { SuggestedLoadChange } from 'types/suggestions/LoadSuggestions';
import { SuggestedQuoteChange } from 'types/suggestions/QuoteSuggestions';

import { filterSuggestions } from './filterSuggestions';
import {
  getLoadViewSuggestionUnsupportedReason,
  isLoadViewSuggestionUnsupportedForTms,
} from './loadViewSuggestionTmsSupport';

export {
  getLoadViewSuggestionUnsupportedReason,
  isLoadViewSuggestionUnsupportedForTms,
} from './loadViewSuggestionTmsSupport';

// Quick Quote SPECIAL transport must not be applied into the form (team review).
export function isBlockedSpecialTransportSuggestion(
  suggestion: GenericSuggestion
): boolean {
  if (suggestion.pipeline !== SuggestionPipelines.QuickQuote) {
    return false;
  }
  return (
    (suggestion as SuggestedQuoteChange).suggested?.transportType ===
    TransportType.SPECIAL
  );
}

// True when the suggestion cannot be applied for policy or TMS-capability reasons.
export function isKitSuggestionApplyBlocked(
  suggestion: GenericSuggestion,
  tmsName?: Maybe<string>
): boolean {
  return (
    isBlockedSpecialTransportSuggestion(suggestion) ||
    isLoadViewSuggestionUnsupportedForTms(suggestion, tmsName ?? null)
  );
}

export function getKitSuggestionApplyBlockedReason(
  suggestion: GenericSuggestion,
  tmsName?: Maybe<string>
): Maybe<string> {
  if (isBlockedSpecialTransportSuggestion(suggestion)) {
    return 'For special transport types, consult your team to ensure proper handling.';
  }
  return getLoadViewSuggestionUnsupportedReason(suggestion, tmsName ?? null);
}

export type KitSuggestionFeatureFlags = {
  isCarrierInfoSuggestionsEnabled: boolean;
  isAppointmentSuggestionsEnabled: boolean;
  isLoadBuildingEnabled: boolean;
  isLTLQuickQuoteEnabled: boolean;
  isFTLQuickQuoteEnabled: boolean;
};

// Whether a suggestion should appear in Kit given current service feature flags.
export function isKitSuggestionFeatureEnabled(
  suggestion: GenericSuggestion,
  flags: KitSuggestionFeatureFlags
): boolean {
  switch (suggestion.pipeline) {
    case SuggestionPipelines.CarrierInfo:
      return flags.isCarrierInfoSuggestionsEnabled;
    case SuggestionPipelines.ApptConfirmation:
      return flags.isAppointmentSuggestionsEnabled;
    case SuggestionPipelines.LoadBuilding:
      return flags.isLoadBuildingEnabled;
    case SuggestionPipelines.QuickQuote: {
      // The backend sends "TL", "LTL" or an empty mode (TL). Unknown modes stay visible.
      const mode = parseSuggestedQuoteMode(
        (suggestion as SuggestedQuoteChange).suggested?.mode
      );
      if (mode === QuoteMode.LTL) {
        return flags.isLTLQuickQuoteEnabled;
      }
      if (mode === QuoteMode.TL) {
        return flags.isFTLQuickQuoteEnabled;
      }
      return true;
    }
    default:
      return true;
  }
}

// Strip falsy suggested fields and normalize Check Call nested changes.
export function sanitizeKitSuggestion(
  suggestion: GenericSuggestion
): GenericSuggestion {
  if (suggestion.pipeline === SuggestionPipelines.CheckCall) {
    const checkCallChanges = Object.fromEntries(
      Object.entries(suggestion.suggested.checkCallChanges).filter(
        ([_, v]) => !!v
      )
    );
    return {
      ...suggestion,
      suggested: {
        checkCallChanges,
      },
    };
  }

  const truthyFilterSuggestions = filterSuggestions(suggestion.suggested);

  if (suggestion.pipeline === SuggestionPipelines.LoadBuilding) {
    return {
      ...suggestion,
      suggested: truthyFilterSuggestions,
    } as LoadBuildingSuggestions;
  }

  if (
    suggestion.pipeline === SuggestionPipelines.CarrierInfo ||
    suggestion.pipeline === SuggestionPipelines.ApptConfirmation
  ) {
    return {
      ...suggestion,
      suggested: truthyFilterSuggestions,
    } as SuggestedLoadChange;
  }

  return {
    ...suggestion,
    suggested: truthyFilterSuggestions,
  } as SuggestedQuoteChange;
}
